import logger from './logger.js';

const log = logger.withContext('CSRFManager');

/**
 * Gestionnaire de tokens CSRF pour l'application
 * Gère la récupération, le stockage et l'utilisation des tokens CSRF
 */
class CSRFManager {
    constructor() {
        this.token = null;
        this.tokenExpiry = null;
        this.fetchPromise = null;
        // Le token est valide pendant 1 heure, on le renouvelle après 50 minutes
        this.TOKEN_LIFETIME = 50 * 60 * 1000; // 50 minutes
    }

    /**
     * Vérifie si le token actuel est toujours valide
     */
    isTokenValid() {
        if (!this.token || !this.tokenExpiry) {
            return false;
        }
        return Date.now() < this.tokenExpiry;
    }

    /**
     * Récupère un nouveau token CSRF depuis l'API
     * @param {string} apiBaseUrl - URL de base de l'API
     * @returns {Promise<string>} Le token CSRF
     */
    async fetchToken(apiBaseUrl) {
        // Si une requête est déjà en cours, attendre son résultat
        if (this.fetchPromise) {
            log.debug('CSRF token fetch already in progress, waiting...');
            return this.fetchPromise;
        }

        // Si le token est toujours valide, le retourner
        if (this.isTokenValid()) {
            log.debug('Using cached CSRF token');
            return this.token;
        }

        log.info('Fetching new CSRF token from API');

        // Créer une nouvelle promesse de récupération
        this.fetchPromise = this._doFetchToken(apiBaseUrl)
            .finally(() => {
                this.fetchPromise = null;
            });

        return this.fetchPromise;
    }

    /**
     * Effectue la récupération réelle du token
     * @private
     */
    async _doFetchToken(apiBaseUrl) {
        try {
            const url = `${apiBaseUrl}/api/csrf-token`;

            log.debug('Requesting CSRF token', { url });

            const response = await fetch(url, {
                method: 'GET',
                credentials: 'include', // Important: envoyer les cookies
                headers: {
                    'Accept': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch CSRF token: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();

            if (!data.csrfToken) {
                throw new Error('CSRF token not found in response');
            }

            this.token = data.csrfToken;
            this.tokenExpiry = Date.now() + this.TOKEN_LIFETIME;

            log.info('CSRF token fetched successfully');
            log.debug('Token details', {
                tokenLength: this.token.length,
                expiresAt: new Date(this.tokenExpiry).toISOString(),
            });

            return this.token;
        } catch (error) {
            log.error('Failed to fetch CSRF token', error);
            // En cas d'erreur, on réinitialise le token
            this.token = null;
            this.tokenExpiry = null;
            throw error;
        }
    }

    /**
     * Obtient le token CSRF actuel, en le récupérant si nécessaire
     * @param {string} apiBaseUrl - URL de base de l'API
     * @returns {Promise<string>} Le token CSRF
     */
    async getToken(apiBaseUrl) {
        return this.fetchToken(apiBaseUrl);
    }

    /**
     * Invalide le token actuel (par exemple, après une déconnexion)
     */
    invalidate() {
        log.info('Invalidating CSRF token');
        this.token = null;
        this.tokenExpiry = null;
        this.fetchPromise = null;
    }

    /**
     * Vérifie si une méthode HTTP nécessite un token CSRF
     * @param {string} method - La méthode HTTP (GET, POST, etc.)
     * @returns {boolean}
     */
    requiresToken(method) {
        if (!method) return false;
        const upperMethod = method.toUpperCase();
        // Le serveur ignore GET, HEAD, OPTIONS
        return !['GET', 'HEAD', 'OPTIONS'].includes(upperMethod);
    }
}

// Instance singleton
const csrfManager = new CSRFManager();

export default csrfManager;

